import Vue from "vue";
import { GetterTree, ActionTree, MutationTree } from "vuex";
import { IState } from "@/store";
import { Methods } from "@/models/methods";
import objectHash from "object-hash";
import _ from "@/boot/lodash";
import { AxiosRequestConfig } from "axios";

export enum DataModules {
  ACCOUNTS = "accounts",
  AFFILIATES = "affiliates",
  AFFILIATES_COMMISSIONS = "affiliates/commissions",
  AFFILIATES_LINKS = "affiliates/links",
  AFFILIATES_PAYOUTS = "affiliates/payouts",
  AFFILIATES_REFERRALS = "affiliates/referrals",
  AFFILIATES_PAYOUT_DESTINATIONS = "affiliates/payoutDestinations",
  AFFILIATES_STATS = "affiliates/stats",
  AFFILIATES_TIERS = "affiliates/tiers",
  AFFILIATES_TIERS_CONDITIONS = "affiliates/tiers/conditions",
  AFFILIATES_TIERS_CONDITIONS_PRODUCTS = "affiliates/tiers/conditions/products",
  BASKETS = "baskets",
  BRANDS = "brands",
  BRAND_GATEWAYS = "brandGateways",
  BULK_ACTIONS = "bulkActions",
  BULK_ACTIONS_RESULTS = "bulkActions/results",
  CARD_TYPES = "cardTypes",
  CATALOGUE = "catalogue",
  CATALOGUE_CATEGORIES = "catalogue/categories",
  CATALOGUE_PROMOTIONS = "catalogue/promotions",
  CLIENTS = "clients",
  CLIENTS_ACCOUNTS = "clients/accounts",
  CLIENTS_ADDRESSES = "clients/addresses",
  CLIENTS_COMPANIES = "clients/companies",
  CLIENTS_EMAILS = "clients/emails",
  CLIENTS_NOTES = "clients/notes",
  CLIENTS_PAYMENT_DETAILS = "clients/paymentDetails",
  CLIENTS_PHONES = "clients/phones",
  CLIENT_TEMPLATES = "clientTemplates",
  CONTRACTS = "contracts",
  CONTRACTS_CATEGORIES = "contracts/categories",
  CONTRACTS_PRODUCTS = "contracts/products",
  CUSTOM_FIELDS = "customFields",
  CUSTOM_FIELDS_BASKET = "customFields/basket",
  CUSTOM_FIELDS_CLIENTS = "customFields/clients",
  DELEGATES = "delegates",
  EMAILS = "emails",
  FRAUD_DETECTION = "fraudDetection",
  FRAUD_DETECTION_EVENTS = "fraudDetection/events",
  FRAUD_DETECTION_POLICIES = "fraudDetection/policies",
  GATEWAYS = "gateways",
  GATEWAYS_PROVIDERS = "gatewayProviders",
  HOOKS_LOGS = "hooksLogs",
  HOST_MODULE = "web_hosting",
  HOST_MODULE_TLDS = "web_hosting/tlds",
  IMAGES = "images",
  IMPORTS = "imports",
  IMPORTS_LOGS = "imports/logs",
  IMPORT_MAPPINGS = "imports/mappings",
  INVOICES = "invoices",
  INVOICES_BASKETS = "invoices/baskets",
  INVOICES_PDFS = "invoices/pdfs",
  LEADS = "leads",
  LEADS_NOTES = "leads/notes",
  LEGACY_INVOICES = "legacyInvoices",
  NOTIFICATIONS_CATEGORIES = "notifications/categories",
  NOTIFICATIONS_CHANNELS = "notifications/channels",
  NOTIFICATIONS_HOOKS = "notifications/hooks",
  NOTIFICATIONS_RECIPIENT_TYPES = "notifications/channels",
  NOTIFICATIONS_TEMPLATES = "notifications/templates",
  NOTIFICATIONS_TEMPLATES_CONTENTS = "notifications/templatesContents",
  OAUTH = "oauth",
  OAUTH_AUTHORIZATIONS = "oauth/authorizations",
  OAUTH_CLIENTS = "oauthClients",
  OAUTH_TOKENS = "oauth/tokens",
  ORDERS = "orders",
  ORDERS_TEMPLATES = "orders/templates",
  OBJECT_NOTIFICATIONS = "objectNotifications",
  PAYMENTS = "payments",
  PAYMENT_DETAILS = "paymentDetails",
  PRICELISTS = "pricelists",
  PRIORITIES = "priorities",
  PROVISIONING = "provisioning",
  PROVISIONING_BASKET_PRODUCTS = "provisioning/basketProducts",
  PROVISIONING_BLUEPRINTS = "provisioning/blueprints",
  PROVISIONING_BRAND = "provisioning/brand",
  PROVISIONING_CATEGORIES = "provisioning/categories",
  PROVISIONING_CONFIGURATIONS = "provisioning/configurations",
  PROVISIONING_CONTRACT_PRODUCTS = "provisioning/contractProducts",
  PROVISIONING_PROVIDERS = "provisioning/providers",
  SEGMENTS = "segments",
  SEGMENTS_RESULTS = "segments/results",
  SETTINGS = "settings",
  SETTINGS_CATEGORIES = "settings/categories",
  SHARES = "shares",
  STATS = "stats",
  STATUSES = "statuses",
  TAGS = "tags",
  TEMPLATE = "template",
  TERMS_AND_CONDITIONS = "termsAndConditions",
  TICKETS = "tickets",
  TICKETS_DEPARTMENTS = "tickets/departments",
  TICKETS_FILES = "tickets/files",
  TICKETS_MESSAGES = "tickets/messages",
  TRANSLATIONS = "translations",
  UPM_TACKING = "upmTracking",
  USERS = "users",
  USER_GROUPS = "userGroups",
  USER_NOTIFICATIONS = "userNotifications",
  WALLETS = "wallets"
}

export const DEFAULT_LIST_SCOPE = "$list";
export const DEFAULT_SINGLE_SCOPE = "$single";

export interface IDataStateElement {
  data: {};
  params: {};
  hash: string;
  dataUsage: number;
  total?: number;
}

export interface IDataState {
  [scope: string]: IDataStateElement;
}

export interface IDataModuleState {
  [module: string]: IDataState;
}

const moduleGetters: GetterTree<IDataModuleState, IState> = {
  list: state => ({ storeModule, scope }) => {
    scope = scope || DEFAULT_LIST_SCOPE;
    const dataPath = [storeModule, scope, "data"].join("/").replace(/\//g, ".");
    return _.get(state, dataPath, null);
  },
  listArray: (state, getters) => ({ storeModule, scope }) => {
    scope = scope || DEFAULT_LIST_SCOPE;
    return _.orderBy(_.values(getters.list({ storeModule, scope })), "_order");
  },
  total: state => ({ storeModule, scope }) => {
    scope = scope || DEFAULT_LIST_SCOPE;
    const dataPath = [storeModule, scope, "total"]
      .join("/")
      .replace(/\//g, ".");
    return _.get(state, dataPath, 0);
  },
  single: state => ({ storeModule, scope }) => {
    scope = scope || DEFAULT_SINGLE_SCOPE;
    const dataPath = [storeModule, scope, "data"].join("/").replace(/\//g, ".");
    return _.get(state, dataPath, null);
  }
};

const actions: ActionTree<IDataModuleState, IState> = {
  callApi: (
    { dispatch },
    {
      callConfig = new Object(),
      method,
      path,
      requestConfig,
      returnData = true,
      vm
    }: {
      callConfig?: object;
      method: Methods;
      path: string;
      requestConfig: AxiosRequestConfig;
      returnData: boolean;
      vm?: Vue;
    }
  ) => {
    return new Promise((resolve, reject) => {
      dispatch(
        "api/call",
        {
          method,
          path,
          requestConfig,
          callConfig: { vueInstance: vm, ...callConfig }
        },
        { root: true }
      )
        .then(response => resolve(returnData ? response.data : response))
        .catch(reject);
    });
  },
  fetch: (
    { commit, dispatch, state, rootState },
    {
      callConfig = new Object(),
      handle401Error = true,
      ignoreStored = false,
      merge = false,
      method = Methods.GET,
      params,
      path,
      returnData = true,
      scope,
      storeData = true,
      storeModule,
      vm
    }: {
      callConfig?: object;
      handle401Error: boolean;
      ignoreStored: boolean;
      merge: boolean;
      method: Methods;
      params: any;
      path: string;
      returnData: boolean;
      scope: string;
      storeData: boolean;
      storeModule: DataModules;
      vm?: Vue;
    }
  ) => {
    const dataHash = objectHash.sha1({
      params,
      path,
      brandId: rootState.auth.admin.brandId
    });
    const dataPath = [storeModule, scope].join("/").replace(/\//g, ".");
    return new Promise((resolve, reject) => {
      if (!ignoreStored && _.get(state, `${dataPath}.hash`) === dataHash) {
        return resolve({
          data: _.get(state, `${dataPath}.data`),
          params: params,
          total: _.get(state, `${dataPath}.total`),
          messages: _.get(state, `${dataPath}.messages`)
        });
      }
      dispatch("callApi", {
        callConfig,
        method,
        path,
        requestConfig: { params, handle401Error },
        returnData: false,
        vm
      })
        .then(resolve)
        .catch(reject);
    }).then(result => {
      const data = returnData ? _.get(result, "data", null) : result;
      if (!storeData) {
        return data;
      }

      commit(`setData`, {
        storeModule,
        scope,
        merge,
        data: _.get(result, "data"),
        total: _.get(result, "total"),
        messages: _.get(result, "messages", null),
        params: params,
        hash: dataHash
      });

      commit(`setDependant`, {
        storeModule,
        scope,
        dependant: _.get(vm, "$data.$cuid", "CUID_UNSET")
      });
      return data;
    });
  },
  search: ({ dispatch }, payload) => {
    return dispatch("fetch", {
      ...payload,
      scope: _.get(payload, "scope") || DEFAULT_LIST_SCOPE
    });
  },
  list: ({ dispatch }, payload) => {
    return dispatch("fetch", {
      ...payload,
      scope: _.get(payload, "scope") || DEFAULT_LIST_SCOPE
    });
  },
  get: ({ dispatch }, payload) => {
    return dispatch("fetch", {
      ...payload,
      scope: _.get(payload, "scope") || DEFAULT_SINGLE_SCOPE
    });
  },
  create: (
    { dispatch },
    {
      path,
      data,
      vm
    }: {
      path: string;
      data: any;
      vm?: Vue;
    }
  ) => {
    return dispatch("callApi", {
      method: Methods.POST,
      path,
      requestConfig: { data },
      vm
    });
  },
  update: (
    { dispatch, commit },
    {
      path,
      data,
      storeModule,
      key,
      scope,
      vm
    }: {
      path: string;
      data: any;
      storeModule: DataModules;
      key?: string;
      scope?: string;
      vm?: Vue;
    }
  ) => {
    return new Promise((resolve, reject) => {
      dispatch("callApi", {
        method: Methods.PUT,
        path,
        requestConfig: { data },
        vm
      })
        .then(result => {
          if (scope) {
            commit(`updateData`, {
              storeModule,
              scope,
              data,
              key
            });
          }
          resolve(result);
        })
        .catch(reject);
    });
  },
  remove: (
    { dispatch, commit },
    {
      path,
      data,
      storeModule,
      scope,
      keys = [],
      vm
    }: {
      path: string;
      data: any;
      storeModule: DataModules;
      scope?: string;
      keys?: string[];
      vm?: Vue;
    }
  ) => {
    return new Promise((resolve, reject) => {
      dispatch("callApi", {
        method: Methods.DELETE,
        path,
        requestConfig: { data },
        vm
      })
        .then(result => {
          if (scope) {
            commit(`binData`, {
              scope,
              keys,
              storeModule
            });
          }
          resolve(result);
        })
        .catch(reject);
    });
  },
  binList: ({ dispatch }, payload) => {
    return dispatch("bin", {
      ...payload,
      scope: payload.scope || DEFAULT_LIST_SCOPE
    });
  },
  binSingle: ({ dispatch }, payload) => {
    return dispatch("bin", {
      ...payload,
      scope: payload.scope || DEFAULT_SINGLE_SCOPE
    });
  },
  bin: (
    { state, commit },
    {
      storeModule,
      scope,
      vm,
      ignoreDependants,
      keys = []
    }: {
      storeModule: DataModules;
      scope: string;
      vm?: Vue;
      ignoreDependants?: boolean;
      keys?: string[];
    }
  ) => {
    const dependant = _.get(vm, "$data.$cuid", "CUID_UNSET");
    const dependants = _.get(
      state,
      `${storeModule.replace(/\//g, ".")}.${scope}.dependants`,
      []
    );
    _.pull(dependants as [], dependant); // Mutate array (removing dependants)
    if (dependants.length && !ignoreDependants) {
      commit("unsetDependant", { storeModule, scope, dependant });
    } else {
      commit("binData", { storeModule, scope, keys });
    }
  }
};

// Reactively set nested props

export function setProp(obj, props, value) {
  const prop = props.shift();
  if (!obj[prop]) {
    Vue.set(obj, prop, {});
  }
  if (!props.length) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      Vue.set(obj, prop, { ...obj[prop], ...value });
    } else {
      Vue.set(obj, prop, value);
    }
    return;
  }
  setProp(obj[prop], props, value);
}

// Reactively delete nested props

export function deleteProp(obj, props) {
  const prop = props.shift();
  if (!obj[prop]) {
    return;
  }
  if (!props.length) {
    Vue.delete(obj, prop);
    return;
  }
  deleteProp(obj[prop], props);
}

const mutations: MutationTree<IDataModuleState> = {
  setData: (
    state,
    {
      storeModule,
      scope,
      total,
      messages = null,
      hash,
      data,
      params,
      merge = false
    }
  ) => {
    const dataPath = [storeModule, scope].join("/").replace(/\//g, ".");
    let scopeData = _.get(state, dataPath, {});
    const newScopeData = {};
    _.set(newScopeData, "total", total);
    _.set(newScopeData, "params", params);
    _.set(newScopeData, "hash", hash);
    _.set(newScopeData, "messages", messages);
    _.set(
      newScopeData,
      "data",
      _.isArray(data)
        ? _.keyBy(
            _.map(data, (i, index) => ({ ...i, _order: index })),
            "id"
          )
        : data
    );

    if (merge) {
      _.merge(scopeData, newScopeData);
    } else {
      scopeData = newScopeData;
    }

    setProp(state, dataPath.split("."), scopeData);
  },
  updateData: (state, { storeModule, scope, key = "", data }) => {
    const dataPath = [storeModule, scope].join("/").replace(/\//g, ".");
    const scopeData = _.get(state, dataPath, {});
    // Potential BUG, if want to remove some properties
    _.merge(
      _.toString(key).length
        ? _.get(scopeData, `data.${key}`, {})
        : _.get(scopeData, `data`, {}),
      data
    ); // Mutate object
    setProp(state, dataPath.split("."), scopeData);
  },
  replaceData: (state, { storeModule, scope, data }) => {
    const dataPath = [storeModule, scope, "data"].join("/").replace(/\//g, ".");
    setProp(state, dataPath.split("."), data);
  },
  replaceByPath: (state, { storeModule, scope, path = "", data }) => {
    const dataPath = [storeModule, scope, path].join("/").replace(/\//g, ".");
    setProp(state, dataPath.split("."), data);
  },
  binData: (state, { storeModule, scope, keys = [] }) => {
    if (_.isEmpty(keys)) {
      const dataPath = [storeModule, scope].join("/").replace(/\//g, ".");
      deleteProp(state, dataPath.split("."));
    } else {
      keys.forEach(key => {
        const dataPath = [storeModule, scope, `data.${key}`]
          .join("/")
          .replace(/\//g, ".");
        deleteProp(state, dataPath.split("."));
        const totalPath = [storeModule, scope, "total"]
          .join("/")
          .replace(/\//g, ".");
        const currentTotal = _.toNumber(_.get(state, totalPath, 0));
        setProp(
          state,
          totalPath.split("."),
          currentTotal ? currentTotal - 1 : currentTotal
        );
      });
    }
  },
  binSingleItemList: (state, { storeModule, scope, path }) => {
    scope = scope || DEFAULT_LIST_SCOPE;
    const dataPath = [storeModule, scope, path].join("/").replace(/\//g, ".");
    deleteProp(state, dataPath.split("."));
    const totalPath = [storeModule, scope, "total"]
      .join("/")
      .replace(/\//g, ".");
    const currentTotal = _.toNumber(_.get(state, totalPath, 0));
    setProp(
      state,
      totalPath.split("."),
      currentTotal ? currentTotal - 1 : currentTotal
    );
  },
  addSingleItemList: (state, { storeModule, scope, path, data }) => {
    scope = scope || DEFAULT_LIST_SCOPE;
    let dataPath = [storeModule, scope, path].join("/").replace(/\//g, ".");
    setProp(state, dataPath.split("."), data);

    dataPath = [storeModule, scope, "data"].join("/").replace(/\//g, ".");
    const scopeData = _.get(state, dataPath, {});
    const totalPath = [storeModule, scope, "total"]
      .join("/")
      .replace(/\//g, ".");
    setProp(state, totalPath.split("."), _.size(scopeData));
  },
  updateSingleItemList: (state, { storeModule, scope, path, data }) => {
    scope = scope || DEFAULT_LIST_SCOPE;
    const dataPath = [storeModule, scope, path].join("/").replace(/\//g, ".");
    setProp(state, dataPath.split("."), data);
  },
  setDependant: (state, { storeModule, scope, dependant }) => {
    const dataPath = [storeModule, scope].join("/").replace(/\//g, ".");
    const scopeData = _.get(state, dataPath, {});
    const dependants = _.get(scopeData, `dependants`, []);
    _.set(scopeData, "dependants", _.union(dependants, [dependant]));
    setProp(state, dataPath.split("."), scopeData);
  },
  unsetDependant: (state, { storeModule, scope, dependant }) => {
    const dataPath = [storeModule, scope].join("/").replace(/\//g, ".");
    const scopeData = _.get(state, dataPath, {});
    const dependants = _.get(scopeData, `dependants`, []);
    _.pull(dependants, dependant); // Mutate array (removing dependant)
    _.set(scopeData, "dependants", dependants);
    setProp(state, dataPath.split("."), scopeData);
  }
};

export default {
  namespaced: true,
  getters: moduleGetters,
  actions,
  mutations,
  modules: {
    accounts: require("./accounts").default,
    affiliates: require("./affiliates").default,
    baskets: require("./baskets").default,
    brandGateways: require("./brandGateways").default,
    brands: require("./brands").default,
    bulkActions: require("./bulkActions").default,
    cardTypes: require("./cardTypes").default,
    catalogue: require("./catalogue").default,
    clientTemplates: require("./clientTemplates").default,
    clients: require("./clients").default,
    contracts: require("./contracts").default,
    customFields: require("./customFields").default,
    delegates: require("./delegates").default,
    emails: require("./emails").default,
    fraudDetection: require("./fraudDetection").default,
    gatewayProviders: require("./gatewayProviders").default,
    gateways: require("./gateways").default,
    hooksLogs: require("./hooksLogs").default,
    images: require("./images").default,
    imports: require("./imports").default,
    invoices: require("./invoices").default,
    leads: require("./leads").default,
    legacyInvoices: require("./legacyInvoices").default,
    notifications: require("./notifications").default,
    oauth: require("./oauth").default,
    oauthClients: require("./oauthClients").default,
    objectNotifications: require("./objectNotifications").default,
    orders: require("./orders").default,
    paymentDetails: require("./paymentDetails").default,
    payments: require("./payments").default,
    pricelists: require("./pricelists").default,
    priorities: require("./priorities").default,
    provisioning: require("./provisioning").default,
    segments: require("./segments").default,
    settings: require("./settings").default,
    shares: require("./shares").default,
    stats: require("./stats").default,
    statuses: require("./statuses").default,
    tags: require("./tags").default,
    template: require("./template").default,
    termsAndConditions: require("./termsAndConditions").default,
    tickets: require("./tickets").default,
    translations: require("./translations").default,
    upmTracking: require("./upmTracking").default,
    userGroups: require("./userGroups").default,
    userNotifications: require("./userNotifications").default,
    users: require("./users").default,
    wallets: require("./wallets").default
  }
};
